import { sfx } from '../../audio/sfx';
import { defineGame } from '../../ui/arcade';
import { rr, text, type ArcadeGame, type Button } from '../game';

const held = () => ({ left: false, right: false });

/** Side-view volleyball: keep the rally going against a friendly CPU. */
function volleyball(): ArcadeGame {
  const W = 320, H = 240, GROUND = 214, NET_X = 160, NET_TOP = 150;
  let px = 80, cpu = 240, bx = 80, by = 80, vx = 0, vy = 0, lives = 3, flash = 0;
  const keys = held();
  const g: ArcadeGame = {
    w: W, h: H, buttons: ['left', 'a', 'right'], score: 0, over: false,
    reset() {
      px = 80; cpu = 240; bx = 80; by = 60; vx = 0; vy = 0; lives = 3; g.score = 0; g.over = false;
    },
    press(b: Button, down: boolean) {
      if (b === 'left') keys.left = down;
      if (b === 'right') keys.right = down;
      if (b === 'a' && down && Math.abs(bx - px) < 26 && by > GROUND - 70 && by < GROUND - 8) {
        vy = -330;
        vx = 150 + Math.random() * 60 + (bx - px) * 2;
        g.score++;
        flash = 0.2;
        sfx('pop');
      }
    },
    update(dt) {
      const sp = 190;
      if (keys.left) px -= sp * dt;
      if (keys.right) px += sp * dt;
      px = Math.max(16, Math.min(NET_X - 14, px));
      vy += 420 * dt;
      bx += vx * dt;
      by += vy * dt;
      // Net.
      if (Math.abs(bx - NET_X) < 4 && by > NET_TOP) {
        vx = -vx * 0.6;
        bx = NET_X + Math.sign(vx || 1) * 5;
      }
      if (bx < 6 || bx > W - 6) vx = -vx;
      // CPU tracks the ball on its side and returns it (a little worse as the rally grows).
      const target = bx > NET_X ? bx : 240;
      cpu += Math.sign(target - cpu) * Math.min(Math.abs(target - cpu), (150 - Math.min(60, g.score * 3)) * dt);
      if (bx > NET_X && Math.abs(bx - cpu) < 24 && by > GROUND - 46 && vy > 0) {
        vy = -320 - Math.random() * 40;
        vx = -(130 + Math.random() * 80);
        sfx('click');
      }
      if (by > GROUND - 4) {
        if (bx < NET_X) {
          lives--;
          sfx('bad');
          if (lives <= 0) g.over = true;
        } else {
          g.score += 2;
          sfx('ok');
        }
        bx = bx < NET_X ? 80 : 240;
        by = 50;
        vx = bx < NET_X ? 0 : -120;
        vy = 0;
      }
      flash = Math.max(0, flash - dt);
    },
    draw(c, t) {
      const sky = c.createLinearGradient(0, 0, 0, H);
      sky.addColorStop(0, '#8fd6ff');
      sky.addColorStop(1, '#fff1c8');
      c.fillStyle = sky;
      c.fillRect(0, 0, W, H);
      c.fillStyle = '#ecd59c';
      c.fillRect(0, GROUND, W, H - GROUND);
      c.fillStyle = 'rgba(255,255,255,0.4)';
      for (let i = 0; i < 4; i++) c.fillRect(((t * 10 + i * 90) % (W + 60)) - 60, 30 + i * 12, 40, 6);
      c.fillStyle = '#2a1810';
      c.fillRect(NET_X - 2, NET_TOP, 4, GROUND - NET_TOP);
      c.fillStyle = 'rgba(255,255,255,0.8)';
      for (let y = NET_TOP; y < NET_TOP + 26; y += 4) c.fillRect(NET_X - 8, y, 16, 1);
      kid(c, px, GROUND, '#e2493b', flash > 0);
      kid(c, cpu, GROUND, '#3a8fd9', false);
      c.fillStyle = 'rgba(0,0,0,0.2)';
      c.beginPath();
      c.ellipse(bx, GROUND, 6, 2, 0, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = '#ffffff';
      c.beginPath();
      c.arc(bx, by, 7, 0, Math.PI * 2);
      c.fill();
      c.strokeStyle = '#ffd34e';
      c.lineWidth = 1.5;
      c.beginPath();
      c.arc(bx, by, 7, t * 4, t * 4 + Math.PI);
      c.stroke();
      text(c, '❤️'.repeat(lives), 10, 14, 12, '#fff', 'left');
    },
    stars: (s) => Math.min(6, Math.floor(s / 4)),
  };
  return g;
}

function kid(c: CanvasRenderingContext2D, x: number, ground: number, shirt: string, jump: boolean): void {
  const y = ground - (jump ? 6 : 0);
  c.fillStyle = 'rgba(0,0,0,0.2)';
  c.beginPath();
  c.ellipse(x, ground, 12, 3, 0, 0, Math.PI * 2);
  c.fill();
  rr(c, x - 7, y - 26, 14, 18, 5, shirt);
  rr(c, x - 6, y - 10, 5, 10, 2, '#2f3442');
  rr(c, x + 1, y - 10, 5, 10, 2, '#2f3442');
  c.fillStyle = '#f6cfa8';
  c.beginPath();
  c.arc(x, y - 34, 9, 0, Math.PI * 2);
  c.fill();
  c.fillStyle = '#3a2418';
  c.beginPath();
  c.arc(x, y - 37, 9, Math.PI, 0);
  c.fill();
  rr(c, x - 12, y - 26 - (jump ? 10 : 0), 5, 12, 2, '#f6cfa8');
  rr(c, x + 7, y - 26 - (jump ? 10 : 0), 5, 12, 2, '#f6cfa8');
}

/** Top-down tennis: slide to the ball and swing at the right moment. */
function tennis(): ArcadeGame {
  const W = 260, H = 360, PY = 320;
  let px = 130, bx = 130, by = 40, vx = 0, vy = 140, lives = 3, swing = 0;
  const keys = held();
  const g: ArcadeGame = {
    w: W, h: H, buttons: ['left', 'a', 'right'], score: 0, over: false,
    reset() {
      px = 130; bx = 130; by = 40; vx = (Math.random() - 0.5) * 120; vy = 140; lives = 3; g.score = 0; g.over = false;
    },
    press(b, down) {
      if (b === 'left') keys.left = down;
      if (b === 'right') keys.right = down;
      if (b === 'a' && down) {
        swing = 0.25;
        if (vy > 0 && Math.abs(by - PY) < 26 && Math.abs(bx - px) < 30) {
          vy = -(170 + g.score * 8);
          vx = (bx - px) * 4 + (Math.random() - 0.5) * 60;
          g.score++;
          sfx('pop');
        }
      }
    },
    update(dt) {
      const sp = 200;
      if (keys.left) px -= sp * dt;
      if (keys.right) px += sp * dt;
      px = Math.max(20, Math.min(W - 20, px));
      bx += vx * dt;
      by += vy * dt;
      if (bx < 18 || bx > W - 18) vx = -vx;
      if (by < 40 && vy < 0) {
        // The friend at the top always returns it, aiming somewhere new.
        vy = 140 + g.score * 7;
        vx = (Math.random() - 0.5) * (120 + g.score * 10);
        sfx('click');
      }
      if (by > H + 10) {
        lives--;
        sfx('bad');
        if (lives <= 0) g.over = true;
        bx = 130;
        by = 40;
        vx = (Math.random() - 0.5) * 100;
        vy = 140;
      }
      swing = Math.max(0, swing - dt);
    },
    draw(c, t) {
      c.fillStyle = '#3f9a6a';
      c.fillRect(0, 0, W, H);
      c.fillStyle = '#3f7fbf';
      c.fillRect(18, 20, W - 36, H - 40);
      c.strokeStyle = '#ffffff';
      c.lineWidth = 2;
      c.strokeRect(18, 20, W - 36, H - 40);
      c.beginPath();
      c.moveTo(W / 2, 20);
      c.lineTo(W / 2, H - 20);
      c.stroke();
      c.fillStyle = 'rgba(255,255,255,0.85)';
      c.fillRect(10, H / 2 - 2, W - 20, 4);
      // Friend at the top.
      rr(c, bx - 8 + Math.sin(t * 3) * 3, 14, 16, 12, 4, '#ffd34e');
      // Ball with shadow (height grows mid-court).
      const lift = Math.sin(Math.max(0, Math.min(1, (by - 40) / (PY - 40))) * Math.PI) * 14;
      c.fillStyle = 'rgba(0,0,0,0.25)';
      c.beginPath();
      c.ellipse(bx, by + 4, 5, 2, 0, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = '#e8ff5a';
      c.beginPath();
      c.arc(bx, by - lift, 5, 0, Math.PI * 2);
      c.fill();
      // You and your racket.
      rr(c, px - 9, PY - 8, 18, 16, 6, '#e2493b');
      c.save();
      c.translate(px + 12, PY - 2);
      c.rotate(swing > 0 ? -1.2 + swing * 6 : -0.4);
      rr(c, -2, -18, 4, 14, 2, '#5e381f');
      c.strokeStyle = '#ffffff';
      c.lineWidth = 2;
      c.beginPath();
      c.ellipse(0, -24, 7, 9, 0, 0, Math.PI * 2);
      c.stroke();
      c.restore();
      text(c, '❤️'.repeat(lives), 24, 34, 12, '#fff', 'left');
    },
    stars: (s) => Math.min(6, Math.floor(s / 4)),
  };
  return g;
}

/** Batting practice: swing when the pitch reaches the plate. */
function baseball(): ArcadeGame {
  const W = 260, H = 360, PLATE = 300;
  let ball = 0, speed = 0, pitch = 0, state: 'wait' | 'flying' | 'hit' | 'miss' = 'wait', wait = 1, msg = '', msgT = 0, swing = 0, curve = 0, hitV = { x: 0, y: 0 };
  let bx = 130, by = 70;
  const g: ArcadeGame = {
    w: W, h: H, buttons: ['a'], score: 0, over: false,
    reset() {
      pitch = 0; g.score = 0; g.over = false; state = 'wait'; wait = 1;
    },
    press(b, down) {
      if (b !== 'a' || !down || swing > 0) return;
      swing = 0.3;
      if (state !== 'flying') return;
      const d = Math.abs(by - PLATE);
      if (d < 9) {
        g.score += 4;
        msg = 'HOME RUN!';
        sfx('fanfare');
        hitV = { x: (Math.random() - 0.5) * 120, y: -520 };
        state = 'hit';
      } else if (d < 22) {
        g.score += 1;
        msg = 'HIT!';
        sfx('ok');
        hitV = { x: (Math.random() - 0.5) * 260, y: -300 };
        state = 'hit';
      } else return;
      msgT = 1;
    },
    update(dt) {
      swing = Math.max(0, swing - dt);
      msgT = Math.max(0, msgT - dt);
      if (state === 'wait') {
        wait -= dt;
        if (wait <= 0) {
          if (pitch >= 10) {
            g.over = true;
            return;
          }
          pitch++;
          state = 'flying';
          ball = 0;
          speed = 220 + Math.random() * 120 + pitch * 8;
          curve = (Math.random() - 0.5) * 60;
          bx = 130;
          by = 70;
        }
        return;
      }
      if (state === 'flying') {
        ball += dt;
        by += speed * dt;
        bx = 130 + Math.sin(ball * 3) * curve * 0.3;
        if (by > PLATE + 30) {
          state = 'miss';
          msg = 'STRIKE';
          msgT = 0.8;
          sfx('bad');
          wait = 1;
          state = 'wait';
        }
      } else if (state === 'hit') {
        bx += hitV.x * dt;
        by += hitV.y * dt;
        if (by < -20 || bx < -20 || bx > W + 20) {
          state = 'wait';
          wait = 0.9;
        }
      }
    },
    draw(c) {
      c.fillStyle = '#4f9a47';
      c.fillRect(0, 0, W, H);
      c.fillStyle = '#c9a36a';
      c.beginPath();
      c.moveTo(W / 2, 40);
      c.lineTo(W - 20, 220);
      c.lineTo(W / 2, H - 20);
      c.lineTo(20, 220);
      c.closePath();
      c.fill();
      c.fillStyle = '#4f9a47';
      c.beginPath();
      c.moveTo(W / 2, 90);
      c.lineTo(W - 70, 210);
      c.lineTo(W / 2, H - 70);
      c.lineTo(70, 210);
      c.closePath();
      c.fill();
      // Pitcher and plate.
      rr(c, W / 2 - 8, 56, 16, 18, 5, '#3a8fd9');
      c.fillStyle = '#ffffff';
      c.beginPath();
      c.moveTo(W / 2 - 10, PLATE);
      c.lineTo(W / 2 + 10, PLATE);
      c.lineTo(W / 2 + 10, PLATE + 8);
      c.lineTo(W / 2, PLATE + 14);
      c.lineTo(W / 2 - 10, PLATE + 8);
      c.fill();
      // Batter and bat.
      rr(c, W / 2 + 18, PLATE - 24, 16, 22, 5, '#e2493b');
      c.save();
      c.translate(W / 2 + 18, PLATE - 14);
      c.rotate(swing > 0 ? -2.6 + (0.3 - swing) * 10 : -0.6);
      rr(c, -2, -30, 5, 30, 2, '#a8693a');
      c.restore();
      if (state === 'flying' || state === 'hit') {
        c.fillStyle = '#ffffff';
        c.beginPath();
        c.arc(bx, by, 4.5, 0, Math.PI * 2);
        c.fill();
        c.strokeStyle = '#e2493b';
        c.lineWidth = 0.8;
        c.stroke();
      }
      if (msgT > 0) text(c, msg, W / 2, 150, 26, '#ffd84a');
      text(c, `⚾ ${pitch}/10`, 14, 18, 13, '#fff', 'left');
    },
    stars: (s) => Math.min(6, Math.floor(s / 3)),
  };
  return g;
}

/** Top-down racer: dodge traffic, collect coins; speed keeps climbing. */
function racing(): ArcadeGame {
  const W = 240, H = 360, LANES = [60, 120, 180];
  let lane = 1, x = 120, speed = 160, dist = 0, spawn = 0;
  let things: { lane: number; y: number; kind: 'car' | 'coin'; color: string }[] = [];
  const g: ArcadeGame = {
    w: W, h: H, buttons: ['left', 'right'], score: 0, over: false,
    reset() {
      lane = 1; x = 120; speed = 160; dist = 0; spawn = 0; things = []; g.score = 0; g.over = false;
    },
    press(b, down) {
      if (!down) return;
      if (b === 'left') lane = Math.max(0, lane - 1);
      if (b === 'right') lane = Math.min(2, lane + 1);
      sfx('click');
    },
    swipe(d) {
      if (d === 'left') lane = Math.max(0, lane - 1);
      if (d === 'right') lane = Math.min(2, lane + 1);
    },
    update(dt) {
      speed += dt * 6;
      dist += speed * dt;
      x += (LANES[lane] - x) * Math.min(1, dt * 12);
      spawn -= dt;
      if (spawn <= 0) {
        spawn = Math.max(0.35, 1.1 - speed / 600);
        const l = Math.floor(Math.random() * 3);
        things.push({ lane: l, y: -40, kind: Math.random() < 0.3 ? 'coin' : 'car', color: ['#3a8fd9', '#ffd34e', '#57c278', '#9b5fc0', '#f4f1ea'][Math.floor(Math.random() * 5)] });
      }
      for (const th of things) th.y += (th.kind === 'car' ? speed * 0.55 : speed) * dt;
      for (let i = things.length - 1; i >= 0; i--) {
        const th = things[i];
        if (Math.abs(LANES[th.lane] - x) < 26 && Math.abs(th.y - 300) < 30) {
          if (th.kind === 'coin') {
            g.score += 10;
            sfx('pickup');
            things.splice(i, 1);
            continue;
          }
          sfx('bad');
          g.over = true;
        }
        if (th.y > H + 40) things.splice(i, 1);
      }
      g.score = Math.max(g.score, Math.floor(dist / 50) + Math.floor(g.score / 10) * 10);
    },
    draw(c) {
      c.fillStyle = '#62ab55';
      c.fillRect(0, 0, W, H);
      c.fillStyle = '#4a4b51';
      c.fillRect(30, 0, 180, H);
      c.fillStyle = '#e8e2d4';
      for (const lx of [90, 150]) for (let y = -40 + (dist % 40); y < H; y += 40) c.fillRect(lx - 2, y, 4, 20);
      c.fillStyle = '#ffffff';
      c.fillRect(30, 0, 3, H);
      c.fillRect(207, 0, 3, H);
      for (let y = (dist * 0.5) % 60 - 60; y < H; y += 60) {
        c.fillStyle = '#3f8a3a';
        c.beginPath();
        c.arc(14, y, 10, 0, Math.PI * 2);
        c.arc(226, y + 30, 10, 0, Math.PI * 2);
        c.fill();
      }
      for (const th of things) {
        const tx = LANES[th.lane];
        if (th.kind === 'coin') {
          c.fillStyle = '#ffd34e';
          c.beginPath();
          c.arc(tx, th.y, 9, 0, Math.PI * 2);
          c.fill();
          c.fillStyle = '#c8901a';
          c.fillRect(tx - 1.5, th.y - 5, 3, 10);
        } else car(c, tx, th.y, th.color, true);
      }
      car(c, x, 300, '#e2493b', false);
    },
    stars: (s) => Math.min(6, Math.floor(s / 60)),
  };
  return g;
}

function car(c: CanvasRenderingContext2D, x: number, y: number, col: string, down: boolean): void {
  c.fillStyle = 'rgba(0,0,0,0.25)';
  c.fillRect(x - 15, y - 22, 32, 46);
  rr(c, x - 16, y - 24, 32, 46, 8, col);
  rr(c, x - 11, y + (down ? 6 : -16), 22, 10, 3, '#9fd3f0');
  c.fillStyle = '#1b1d24';
  for (const [dx, dy] of [[-18, -16], [14, -16], [-18, 10], [14, 10]]) c.fillRect(x + dx, y + dy, 4, 10);
  c.fillStyle = down ? '#ff3a2a' : '#fff6c0';
  c.fillRect(x - 12, y + (down ? 18 : -24), 6, 3);
  c.fillRect(x + 6, y + (down ? 18 : -24), 6, 3);
}

defineGame('volleyball', volleyball);
defineGame('tennis', tennis);
defineGame('baseball', baseball);
defineGame('racing', racing);
